import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, TestTube, Eye, EyeOff, CheckCircle, XCircle, Info, Share2 } from 'lucide-react';
import { Header } from '../../components/layout/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const MetaCAPIConfig = () => {

  const [config, setConfig] = useState({
    meta_pixel_id: '',
    meta_access_token: '',
    meta_test_event_code: '',
    meta_capi_enabled: false
  });
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('config');

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('tayane_token');
      const response = await fetch(`${API}/api/meta-capi/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig({
          meta_pixel_id: data.meta_pixel_id || '',
          meta_access_token: '',
          meta_test_event_code: data.meta_test_event_code || '',
          meta_capi_enabled: data.meta_capi_enabled || false,
          token_configured: data.meta_access_token_configured
        });
      }
    } catch (error) {
      console.error('Erro ao buscar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('tayane_token');
      const response = await fetch(`${API}/api/meta-capi/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('tayane_token');
      const response = await fetch(`${API}/api/meta-capi/logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('tayane_token');
      const response = await fetch(`${API}/api/meta-capi/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          meta_pixel_id: config.meta_pixel_id || null,
          meta_access_token: config.meta_access_token || null,
          meta_test_event_code: config.meta_test_event_code || null,
          meta_capi_enabled: config.meta_capi_enabled
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Configuração salva com sucesso!' });
        fetchConfig();
      } else {
        setMessage({ type: 'error', text: 'Erro ao salvar configuração' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro de conexão' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('tayane_token');
      const response = await fetch(`${API}/api/meta-capi/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Evento de teste enviado com sucesso! Verifique no Events Manager do Meta.' });
        fetchStats();
      } else {
        setMessage({ type: 'error', text: data.message || 'Erro ao enviar evento de teste' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro de conexão' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center p-8">
          <RefreshCw className="w-6 h-6 animate-spin text-[#a48054]" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="meta-capi-page">
      <Header title="Meta Conversions API" subtitle="Configure o envio de eventos server-side para o Meta/Facebook Ads" />
      <div className="p-6 max-w-4xl">

          {/* Tabs */}
          <div className="flex space-x-4 mb-6 border-b">
            <button
              onClick={() => setActiveTab('config')}
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === 'config'
                  ? 'text-[#a48054] border-b-2 border-[#a48054]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Configuração
            </button>
            <button
              onClick={() => { setActiveTab('stats'); fetchStats(); }}
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === 'stats'
                  ? 'text-[#a48054] border-b-2 border-[#a48054]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Estatísticas
        </button>
        <button
          onClick={() => { setActiveTab('logs'); fetchLogs(); }}
          className={`pb-2 px-1 font-medium transition-colors ${
            activeTab === 'logs'
              ? 'text-[#a48054] border-b-2 border-[#a48054]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Logs
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Config Tab */}
      {activeTab === 'config' && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">O que é a Conversions API?</p>
              <p>
                A Conversions API permite enviar eventos de conversão diretamente do servidor para o Meta,
                melhorando a precisão da atribuição de anúncios, especialmente quando bloqueadores de
                anúncios estão ativos ou cookies são limitados.
              </p>
            </div>
          </div>

          {/* Enable Toggle */}
          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.meta_capi_enabled}
                onChange={(e) => setConfig({ ...config, meta_capi_enabled: e.target.checked })}
                className="w-5 h-5 rounded text-[#a48054] focus:ring-[#a48054]"
              />
              <span className="font-medium text-gray-700">Habilitar Meta Conversions API</span>
            </label>
          </div>

          {/* Pixel ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pixel ID
            </label>
            <input
              type="text"
              value={config.meta_pixel_id}
              onChange={(e) => setConfig({ ...config, meta_pixel_id: e.target.value })}
              placeholder="Ex: 123456789012345"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#a48054] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Encontre em: Meta Business Suite → Events Manager → Data Sources
            </p>
          </div>

          {/* Access Token */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Token
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={config.meta_access_token}
                onChange={(e) => setConfig({ ...config, meta_access_token: e.target.value })}
                placeholder={config.token_configured ? '••••••••••• (já configurado)' : 'Cole o token de acesso'}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#a48054] focus:border-transparent pr-12"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Gere em: Events Manager → Configurações → Conversions API → Generate Access Token
            </p>
          </div>

          {/* Test Event Code */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código de Teste (opcional)
            </label>
            <input
              type="text"
              value={config.meta_test_event_code}
              onChange={(e) => setConfig({ ...config, meta_test_event_code: e.target.value })}
              placeholder="Ex: TEST12345"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#a48054] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use para testar eventos no Events Manager. Deixe vazio em produção.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-[#a48054] text-white rounded-lg hover:bg-[#8a6944] disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Salvando...' : 'Salvar Configuração'}
            </button>
            
            <button
              onClick={handleTest}
              disabled={testing || !config.meta_pixel_id}
              className="flex items-center gap-2 px-6 py-3 border border-[#a48054] text-[#a48054] rounded-lg hover:bg-[#a48054]/10 disabled:opacity-50"
            >
              {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
              {testing ? 'Testando...' : 'Testar Conexão'}
            </button>
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Total de Eventos</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total_events}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">Sucesso</p>
              <p className="text-2xl font-bold text-green-700">{stats.success_events}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">Erros</p>
              <p className="text-2xl font-bold text-red-700">{stats.error_events}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Taxa de Sucesso</p>
              <p className="text-2xl font-bold text-blue-700">
                {stats.total_events > 0 
                  ? Math.round((stats.success_events / stats.total_events) * 100) 
                  : 0}%
              </p>
            </div>
          </div>

          <h3 className="font-medium text-gray-700 mb-3">Eventos por Tipo</h3>
          <div className="space-y-2">
            {Object.entries(stats.events_by_type || {}).map(([type, count]) => (
              <div key={type} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="font-medium">{type}</span>
                <span className="text-gray-600">{count}</span>
              </div>
            ))}
            {Object.keys(stats.events_by_type || {}).length === 0 && (
              <p className="text-gray-500 text-center py-4">Nenhum evento enviado ainda</p>
            )}
          </div>

          {stats.last_event_at && (
            <p className="text-sm text-gray-500 mt-4">
              Último evento: {new Date(stats.last_event_at).toLocaleString('pt-BR')}
            </p>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-gray-700">Últimos Eventos</h3>
            <button
              onClick={fetchLogs}
              className="text-[#a48054] hover:underline text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" /> Atualizar
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.map((log) => (
              <div
                key={log.id}
                className={`p-3 rounded border-l-4 ${
                  log.status === 'success'
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-medium">{log.event_name}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                      log.status === 'success' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                    }`}>
                      {log.status}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(log.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Event ID: {log.event_id}
                </p>
              </div>
            ))}
            {logs.length === 0 && (
              <p className="text-gray-500 text-center py-8">Nenhum log encontrado</p>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MetaCAPIConfig;
